import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet, TouchableOpacity, ScrollView, View, Modal } from 'react-native'
import { RouteProp, useNavigation, useRoute, useTheme } from '@react-navigation/native'
import type { ProfileStackParamList } from '@navigation'
import { Theme } from '@theme'
import {
  Button,
  FormLocation,
  GenericSelectionModal,
  Header,
  Icon,
  IconName,
  KeyboardAwareView,
  Screen,
  Text,
  TextInput,
} from '@components'
import {
  migrateMeetingContextIds,
  useTranslatedContextStore,
  useTranslatedEmotionalContextStore,
  useTranslatedRelationshipStatusStore,
} from '@store'
import { EncounterPlace, getCommonStyles, SelectionItem } from '@utils'
import {
  useGetProfileById,
  usePaginateColorLabels,
  useUpdateProfileEncounterPlace,
  useUpdateProfileContexts,
  useSyncProfileRelationalColors,
} from '@hooks'
import { useLanguage } from '@context'

interface ContextesProps {
  //contact: ContactListItem
}

const SelectedItems = ({
  items,
}: {
  items: { icon?: IconName; color?: string; text: string }[]
}) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)

  return (
    <>
      {items.map((item, i) => (
        <View key={i} style={styles.selectedItemContainer}>
          <View>
            <View style={commonStyles.row}>
              {!!item.icon && (
                <Icon
                  name={item.icon}
                  size={theme.iconSize.large}
                  color={item.color || theme.colors.primary}
                />
              )}
              {!!item.color && !item.icon && (
                <View style={[styles.selectedColor, { backgroundColor: item.color }]} />
              )}
              <Text type="body">{item.text}</Text>
            </View>
          </View>
          <Icon name="editPen" color={theme.colors.lightGrey} size={theme.iconSize.large} />
        </View>
      ))}
    </>
  )
}

export const Contextes: React.FC<ContextesProps> = (
  {
    //contact,
  },
) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const route = useRoute<RouteProp<ProfileStackParamList, 'ProfileContextes'>>()
  const navigation = useNavigation()
  const { t } = useLanguage()
  const profileId = route.params.profileId
  const { data: profile } = useGetProfileById(profileId)
  const { mutate: updateEncounterPlace } = useUpdateProfileEncounterPlace()
  const { mutate: updateProfileContexts } = useUpdateProfileContexts()
  const { mutate: syncRelationalColors } = useSyncProfileRelationalColors()

  const { contexts: groupedContexts } = useTranslatedContextStore()
  const { contexts: availableEmotionalContexts } = useTranslatedEmotionalContextStore()
  const { statuses: availableRelationshipStatus } = useTranslatedRelationshipStatusStore()

  const [searchText, setSearchText] = useState('')
  const {
    flatData: colorLabels,
    isFetching: isFetchingColorLabels,
    isFetchingNextPage: isFetchingNextPageColorLabels,
    fetchNextPage: fetchNextPageColorLabels,
    hasNextPage: hasNextPageColorLabels,
  } = usePaginateColorLabels(searchText)
  const [contexts, setContexts] = useState<SelectionItem[]>([])
  const [emotionalContexts, setEmotionalContexts] = useState<SelectionItem[]>([])
  const [relationshipStatus, setRelationshipStatus] = useState<SelectionItem[]>([])
  const [relationalColor, setRelationalColor] = useState<SelectionItem[]>([])
  const [encounterPlace, setEncounterPlace] = useState<EncounterPlace>()
  const [isContextModalVisible, setIsContextModalVisible] = useState(false)
  const [isEmotionalContextModalVisible, setIsEmotionalContextModalVisible] = useState(false)
  const [isRelationshipStatusModalVisible, setIsRelationshipStatusModalVisible] = useState(false)
  const [isRelationalColorModalVisible, setIsRelationalColorModalVisible] = useState(false)
  const [isLeaveModalVisible, setIsLeaveModalVisible] = useState(false)

  const dirtyRef = useRef(false)
  const profileIdRef = useRef(profileId)
  const pendingRef = useRef({
    meetingContextIds: [] as string[],
    emotionalContextIds: [] as string[],
    relationshipStatusId: null as string | null,
    encounterPlace: undefined as EncounterPlace | undefined,
    relationalColorIds: [] as string[],
    initialRelationalColorIds: [] as string[],
  })
  profileIdRef.current = profileId

  useEffect(() => {
    if (profile?.relationalColors !== undefined) {
      const list = (profile.relationalColors ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        color: r.color,
      }))
      setRelationalColor(list)
      const ids = list.map((r) => r.id)
      pendingRef.current.relationalColorIds = ids
      pendingRef.current.initialRelationalColorIds = ids
    }
  }, [profile?.relationalColors])

  useEffect(() => {
    if (profile?.encounterPlace !== undefined) {
      const val =
        profile.encounterPlace && typeof profile.encounterPlace === 'object'
          ? {
              place: profile.encounterPlace.place ?? '',
              city: profile.encounterPlace.city,
              country: profile.encounterPlace.country,
              state: profile.encounterPlace.state,
              region: profile.encounterPlace.region,
            }
          : undefined
      setEncounterPlace(val)
      pendingRef.current.encounterPlace = val
    }
  }, [profile?.encounterPlace])

  useEffect(() => {
    if (profile?.meetingContextIds && Array.isArray(profile.meetingContextIds)) {
      const allMeeting = groupedContexts.flatMap((g) => g.items)
      // Migre les anciens ids vers les nouveaux avant de résoudre les items.
      const items = migrateMeetingContextIds(profile.meetingContextIds)
        .map((id) => allMeeting.find((c) => c.id === id))
        .filter((c): c is SelectionItem => c != null)
      setContexts(items)
      pendingRef.current.meetingContextIds = items.map((c) => c.id)
    }
  }, [profile?.id, profile?.meetingContextIds?.join(',')])

  useEffect(() => {
    if (profile?.emotionalContextIds && Array.isArray(profile.emotionalContextIds)) {
      const allEmotional = availableEmotionalContexts.flatMap((g) => g.items)
      const items = profile.emotionalContextIds
        .map((id) => allEmotional.find((c) => c.id === id))
        .filter((c): c is SelectionItem => c != null)
      setEmotionalContexts(items)
      pendingRef.current.emotionalContextIds = items.map((c) => c.id)
    }
  }, [profile?.id, profile?.emotionalContextIds?.join(',')])

  useEffect(() => {
    if (profile?.relationshipStatusId != null && profile.relationshipStatusId !== '') {
      const item = availableRelationshipStatus.find((r) => r.id === profile.relationshipStatusId)
      if (item) {
        setRelationshipStatus([item])
        pendingRef.current.relationshipStatusId = item.id
      } else {
        setRelationshipStatus([])
        pendingRef.current.relationshipStatusId = null
      }
    } else {
      setRelationshipStatus([])
      pendingRef.current.relationshipStatusId = null
    }
  }, [profile?.id, profile?.relationshipStatusId])

  // Commit all pending edits. Nothing is persisted until the user explicitly
  // validates (the "Sauvegarde" button, or "Sauvegarder" in the leave dialog).
  const commitSave = () => {
    const pid = profileIdRef.current
    if (!pid || !dirtyRef.current) return
    const p = pendingRef.current
    updateProfileContexts({
      profileId: pid,
      meetingContextIds: p.meetingContextIds,
      emotionalContextIds: p.emotionalContextIds,
      relationshipStatusId: p.relationshipStatusId,
    })
    updateEncounterPlace({ profileId: pid, encounterPlace: p.encounterPlace ?? null })
    if (p.initialRelationalColorIds.length > 0 || p.relationalColorIds.length > 0) {
      syncRelationalColors({
        profileId: pid,
        previousIds: p.initialRelationalColorIds,
        nextIds: p.relationalColorIds,
      })
    }
    // The just-saved colors become the new baseline so a second save in the
    // same session doesn't replay a stale diff.
    p.initialRelationalColorIds = p.relationalColorIds
    dirtyRef.current = false
  }

  const goBack = () => {
    const pid = profileIdRef.current
    if (navigation.canGoBack()) {
      navigation.goBack()
    } else {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'Profile' as never,
            params: { screen: 'DetailsGhostProfile', params: { profileId: pid } },
          },
        ],
      })
    }
  }

  const handleSavePress = () => {
    commitSave()
    goBack()
  }

  // Back arrow: warn before discarding unsaved edits, otherwise just leave.
  const handleBackPress = () => {
    if (dirtyRef.current) {
      setIsLeaveModalVisible(true)
    } else {
      goBack()
    }
  }

  const handleEncounterPlaceChange = (val: EncounterPlace) => {
    setEncounterPlace(val)
    pendingRef.current.encounterPlace = val
    dirtyRef.current = true
  }

  const updatePendingContexts = (updates: {
    meetingContextIds?: string[]
    emotionalContextIds?: string[]
    relationshipStatusId?: string | null
  }) => {
    if (updates.meetingContextIds !== undefined)
      pendingRef.current.meetingContextIds = updates.meetingContextIds
    if (updates.emotionalContextIds !== undefined)
      pendingRef.current.emotionalContextIds = updates.emotionalContextIds
    if (updates.relationshipStatusId !== undefined)
      pendingRef.current.relationshipStatusId = updates.relationshipStatusId
    dirtyRef.current = true
  }

  return (
    <KeyboardAwareView isFullScreen>
      <Screen>
        <Header title={t('contextes.title')} iconLeft="arrowBack" onPressLeft={handleBackPress} />
        <ScrollView contentContainerStyle={styles.container}>
          <TouchableOpacity onPress={() => setIsContextModalVisible(true)} style={styles.gap}>
            <Text type="body">{t('contextes.meetingContext')}</Text>
            {contexts.length === 0 && (
              <TextInput
                placeholder={
                  contexts?.length > 0
                    ? t('contextes.editMeetingContext')
                    : t('contextes.chooseMeetingContext')
                }
                iconLeft="work"
                editable={false}
                pointerEvents="none"
              />
            )}
            {contexts.length > 0 && (
              <SelectedItems items={contexts.map((c) => ({ icon: 'work', text: c.name }))} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setIsEmotionalContextModalVisible(true)}
            style={styles.gap}
          >
            <Text type="body">{t('contextes.associatedEmotion')}</Text>
            {emotionalContexts.length === 0 && (
              <TextInput
                placeholder={
                  emotionalContexts?.length > 0
                    ? t('contextes.editEmotion')
                    : t('contextes.chooseEmotion')
                }
                iconLeft="heart"
                editable={false}
                pointerEvents="none"
              />
            )}
            {emotionalContexts.length > 0 && (
              <SelectedItems
                items={emotionalContexts.map((e) => ({
                  icon: availableEmotionalContexts.find((a) =>
                    a.items.map((item) => item.id).includes(e.id),
                  )?.icon as IconName,
                  text: e.name,
                }))}
              />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setIsRelationshipStatusModalVisible(true)}
            style={styles.gap}
          >
            <Text type="body">{t('contextes.relationshipEvolution')}</Text>
            {relationshipStatus.length === 0 && (
              <TextInput
                placeholder={
                  relationshipStatus?.length > 0
                    ? t('contextes.editRelationship')
                    : t('contextes.chooseRelationship')
                }
                iconLeft="people"
                editable={false}
                pointerEvents="none"
              />
            )}
            {relationshipStatus.length > 0 && (
              <SelectedItems
                items={relationshipStatus.map((r) => ({
                  icon: r.icon,
                  color: r.color,
                  text: r.name,
                }))}
              />
            )}
            {/* Nobody picked this value: say where it comes from, until
                another one is chosen. */}
            {profile?.relationshipStatusIsAutomatic &&
              relationshipStatus[0]?.id === profile.relationshipStatusId && (
                <Text type="bodySmall" color={theme.colors.greyBlue}>
                  {t('contextes.relationshipAutomatic')}
                </Text>
              )}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setIsRelationalColorModalVisible(true)}
            style={[styles.gap, { marginBottom: theme.spacing.sm }]}
          >
            <Text type="body">{t('contextes.associatedColor')}</Text>
            {relationalColor.length === 0 && (
              <TextInput
                placeholder={
                  relationalColor?.length > 0
                    ? t('contextes.editColor')
                    : t('contextes.chooseColor')
                }
                iconLeft="link2"
                editable={false}
                pointerEvents="none"
              />
            )}
            {relationalColor.length > 0 && (
              <SelectedItems
                items={relationalColor.map((r) => ({ color: r.color, text: r.name }))}
              />
            )}
          </TouchableOpacity>
          <FormLocation
            isBigTitle
            title={t('contextes.referencePlace')}
            value={encounterPlace?.place || ''}
            onChange={handleEncounterPlaceChange}
            placeholder={t('contextes.enterPlace')}
            iconRight="search"
          />
          <Button
            title={t('contextes.save')}
            onPress={handleSavePress}
            containerStyle={styles.saveButton}
          />
        </ScrollView>
        <GenericSelectionModal
          isVisible={isContextModalVisible}
          setIsVisible={setIsContextModalVisible}
          title={t('contextes.meetingContext')}
          placeholder={t('contextes.searchMeetingContext')}
          availableItems={groupedContexts.flatMap((g) => g.items)}
          selectedItems={contexts}
          onSelectionChange={(next) => {
            setContexts(next)
            updatePendingContexts({ meetingContextIds: next.map((c) => c.id) })
          }}
          multiSelect={true}
        />

        <GenericSelectionModal
          isVisible={isEmotionalContextModalVisible}
          setIsVisible={setIsEmotionalContextModalVisible}
          title={t('contextes.associatedEmotion')}
          placeholder={t('contextes.searchEmotion')}
          availableItems={availableEmotionalContexts}
          selectedItems={emotionalContexts}
          onSelectionChange={(next) => {
            setEmotionalContexts(next)
            updatePendingContexts({ emotionalContextIds: next.map((c) => c.id) })
          }}
          multiSelect={true}
        />

        <GenericSelectionModal
          isVisible={isRelationshipStatusModalVisible}
          setIsVisible={setIsRelationshipStatusModalVisible}
          title={t('contextes.relationshipEvolution')}
          placeholder={t('contextes.searchRelationship')}
          availableItems={availableRelationshipStatus}
          selectedItems={relationshipStatus}
          multiSelect={false}
          onSelectionChange={(next) => {
            setRelationshipStatus(next)
            updatePendingContexts({ relationshipStatusId: next[0]?.id ?? null })
          }}
        />

        <GenericSelectionModal
          isVisible={isRelationalColorModalVisible}
          setIsVisible={setIsRelationalColorModalVisible}
          title={t('contextes.associatedColor')}
          placeholder={t('contextes.searchColor')}
          availableItems={colorLabels}
          selectedItems={relationalColor}
          onSelectionChange={(nextSelection) => {
            setRelationalColor(nextSelection)
            pendingRef.current.relationalColorIds = nextSelection.map((s) => s.id)
            dirtyRef.current = true
          }}
          isFetching={isFetchingColorLabels}
          fetchNextPage={fetchNextPageColorLabels}
          hasNextPage={hasNextPageColorLabels}
          isFetchingNextPage={isFetchingNextPageColorLabels}
          onSearch={(text) => setSearchText(text)}
        />

        <Modal
          visible={isLeaveModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setIsLeaveModalVisible(false)}
        >
          <View style={styles.overlay}>
            <View style={styles.card}>
              <Text type="subtitleBold">{t('contextes.unsavedTitle')}</Text>
              <Text
                type="bodySmall"
                color={theme.colors.greyBlue}
                style={styles.cardDescription}
              >
                {t('contextes.unsavedMessage')}
              </Text>
              <View style={styles.cardActions}>
                <Button
                  title={t('contextes.save')}
                  onPress={() => {
                    setIsLeaveModalVisible(false)
                    commitSave()
                    goBack()
                  }}
                />
                <Button
                  unfilled
                  title={t('contextes.leaveWithoutSaving')}
                  onPress={() => {
                    setIsLeaveModalVisible(false)
                    dirtyRef.current = false
                    goBack()
                  }}
                />
                <TouchableOpacity
                  style={styles.cancelBtn}
                  onPress={() => setIsLeaveModalVisible(false)}
                >
                  <Text type="body">{t('contextes.keepEditing')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </Screen>
    </KeyboardAwareView>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      padding: theme.spacing.lg,
      gap: theme.spacing.md,
    },
    colorItem: {
      width: 15,
      height: 15,
      borderRadius: 8,
    },
    gap: {
      marginTop: theme.spacing.sm,
      gap: theme.spacing.sm,
    },
    selectedItemContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing.md,
      borderRadius: theme.spacing.xxxl,
      backgroundColor: theme.colors.primary + '10',
    },
    selectedColor: {
      width: 20,
      height: 20,
      borderRadius: 10,
      // Keeps light pastel dots visible on the white background.
      borderWidth: 1,
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    saveButton: {
      marginTop: theme.spacing.lg,
    },
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    card: {
      width: '100%',
      maxWidth: 400,
      backgroundColor: theme.colors.white,
      borderRadius: 16,
      padding: 24,
    },
    cardDescription: {
      marginTop: 8,
    },
    cardActions: {
      marginTop: 20,
      gap: 10,
    },
    cancelBtn: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: 10,
      alignItems: 'center',
    },
  })
